import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export interface PlaceDetails {
  name: string;
  formatted_address: string;
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  formatted_phone_number?: string;
  website?: string;
  opening_hours?: {
    weekday_text?: string[];
    open_now?: boolean;
  };
}

const SEARCH_TIMEOUT_MS = 20000;
const DETAILS_TIMEOUT_MS = 25000;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

export function useGooglePlaces() {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const searchRequestIdRef = useRef(0);
  const detailsRequestIdRef = useRef(0);

  const searchPlaces = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed || trimmed.length < 3) {
      // Cancel any in-flight search UI state
      searchRequestIdRef.current += 1;
      setIsSearching(false);
      setPredictions([]);
      setSearchError(null);
      return;
    }

    const requestId = (searchRequestIdRef.current += 1);

    console.log('[useGooglePlaces] Starting search for:', trimmed);
    setIsSearching(true);
    setSearchError(null);

    try {
      console.log('[useGooglePlaces] Invoking google-places function...');
      const invokePromise = supabase.functions.invoke('google-places', {
        body: { action: 'autocomplete', query: trimmed },
      });

      const { data, error } = await withTimeout(
        invokePromise,
        SEARCH_TIMEOUT_MS,
        'Search timed out. Please try again.',
      );

      // Ignore stale responses
      if (requestId !== searchRequestIdRef.current) return;

      console.log('[useGooglePlaces] Response:', { data, error });

      if (error) {
        console.error('[useGooglePlaces] Supabase function error:', error);
        throw error;
      }

      const resultPredictions = data?.predictions || [];
      console.log('[useGooglePlaces] Setting predictions:', resultPredictions.length);
      setPredictions(resultPredictions);

      if (resultPredictions.length === 0 && trimmed.length >= 3) {
        console.log('[useGooglePlaces] No predictions found for query');
      }
    } catch (error) {
      // Ignore stale errors
      if (requestId !== searchRequestIdRef.current) return;

      const errorMessage = error instanceof Error ? error.message : 'Failed to search places';
      console.error('[useGooglePlaces] Error searching places:', error);
      setSearchError(errorMessage);
      setPredictions([]);
      toast({
        title: 'Search Error',
        description: 'Could not search for places. Please try again.',
        variant: 'destructive',
      });
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsSearching(false);
        console.log('[useGooglePlaces] Search complete');
      }
    }
  }, []);

  const getPlaceDetails = useCallback(async (placeId: string): Promise<PlaceDetails | null> => {
    if (!placeId) return null;

    const requestId = (detailsRequestIdRef.current += 1);

    console.log('[useGooglePlaces] Fetching details for:', placeId);
    setIsFetchingDetails(true);

    try {
      const invokePromise = supabase.functions.invoke('google-places', {
        body: { action: 'details', placeId },
      });

      const { data, error } = await withTimeout(
        invokePromise,
        DETAILS_TIMEOUT_MS,
        'Fetching place details timed out. Please try again.',
      );

      if (requestId !== detailsRequestIdRef.current) return null;

      console.log('[useGooglePlaces] Details response:', { data, error });

      if (error) {
        console.error('[useGooglePlaces] Details error:', error);
        throw error;
      }

      return data?.result || null;
    } catch (error) {
      if (requestId !== detailsRequestIdRef.current) return null;

      console.error('[useGooglePlaces] Error fetching place details:', error);
      toast({
        title: 'Error',
        description: 'Could not fetch place details. Please try again.',
        variant: 'destructive',
      });
      return null;
    } finally {
      if (requestId === detailsRequestIdRef.current) {
        setIsFetchingDetails(false);
      }
    }
  }, []);

  const clearPredictions = useCallback(() => {
    setPredictions([]);
    setSearchError(null);
  }, []);

  const reset = useCallback(() => {
    searchRequestIdRef.current += 1;
    detailsRequestIdRef.current += 1;
    setPredictions([]);
    setIsSearching(false);
    setIsFetchingDetails(false);
    setSearchError(null);
  }, []);

  return {
    predictions,
    isSearching,
    isFetchingDetails,
    searchError,
    searchPlaces,
    getPlaceDetails,
    clearPredictions,
    reset,
  };
}

